(function () {
  'use strict';

  async function registerAzeoWebMCP() {
    if (!document.modelContext || typeof document.modelContext.registerTool !== 'function') return;

    const register = async (tool) => {
      try {
        await document.modelContext.registerTool(tool);
      } catch (error) {
        console.warn('[AZEO WebMCP] Tool registration failed:', tool.name, error);
      }
    };

    const jsonFetch = async (url, options = {}) => {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
        ...options
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(String(data.description || data.message || data.status || ('HTTP ' + response.status)));
      }
      return data;
    };

    const currentProductHandle = () => {
      const match = location.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : null;
    };

    const getProductByHandle = async (handle, signal) => {
      const cleanHandle = String(handle || '').trim().replace(/^\/+|\/+$/g, '');
      if (!cleanHandle) throw new Error('A product handle is required.');
      return jsonFetch('/products/' + encodeURIComponent(cleanHandle) + '.js', { signal });
    };

    const getCurrentProductJson = async (signal) => {
      const handle = currentProductHandle();
      if (!handle) throw new Error('This page is not a Shopify product page.');
      return getProductByHandle(handle, signal);
    };

    const searchCatalogProducts = async (query, limit, signal) => {
      const url = new URL('/search/suggest.json', location.origin);
      url.searchParams.set('q', String(query || '').trim());
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(Math.max(1, Math.min(10, limit || 5))));
      const response = await fetch(url, { credentials: 'same-origin', signal });
      if (!response.ok) throw new Error('Azeo catalog search failed: ' + response.status);
      const data = await response.json();
      return ((((data || {}).resources || {}).results || {}).products || []);
    };

    const isPlaceholderRef = (value) => {
      const raw = String(value == null ? '' : value).trim();
      if (!raw) return false;
      return raw === '0'
        || /^example(?:_string)?$/i.test(raw)
        || /^example[-_ ]/i.test(raw)
        || /ProductVariant\/0$/i.test(raw);
    };

    const parseVariantId = (value) => {
      const raw = String(value == null ? '' : value).trim();
      if (!raw || isPlaceholderRef(raw)) return null;
      const gidMatch = raw.match(/^gid:\/\/shopify\/ProductVariant\/(\d+)$/i);
      if (gidMatch && Number(gidMatch[1]) > 0) return Number(gidMatch[1]);
      if (/^\d+$/.test(raw) && Number(raw) > 0) return Number(raw);
      throw new Error('Variant reference must be a valid Shopify numeric variant ID or ProductVariant GID.');
    };

    const firstUsableVariantId = (product) => {
      const variants = (product && product.variants) || [];
      const variant = variants.find((item) => item.available && Number(item.id) > 0)
        || variants.find((item) => Number(item.id) > 0);
      return variant ? Number(variant.id) : null;
    };

    const resolveVariantId = async (line = {}, signal) => {
      const item = line.item && typeof line.item === 'object' ? line.item : {};
      const explicitId = item.id != null ? item.id : line.variant_id;
      if (explicitId != null && String(explicitId).trim() !== '') {
        const parsed = parseVariantId(explicitId);
        if (parsed) return parsed;
      }

      if (line.handle) {
        const product = await getProductByHandle(line.handle, signal);
        const id = firstUsableVariantId(product);
        if (!id) throw new Error('No available Shopify variant found for handle "' + line.handle + '".');
        return id;
      }

      if (line.query) {
        const results = await searchCatalogProducts(line.query, 5, signal);
        const productResult = results && results[0];
        if (!productResult) throw new Error('No Azeo product matched query "' + line.query + '".');
        let handle = productResult.handle;
        if (!handle && productResult.url) {
          const match = String(productResult.url).match(/\/products\/([^/?#]+)/i);
          handle = match ? decodeURIComponent(match[1]) : null;
        }
        if (!handle) throw new Error('The matching product did not expose a Shopify handle.');
        const product = await getProductByHandle(handle, signal);
        const id = firstUsableVariantId(product);
        if (!id) throw new Error('No available Shopify variant found for query "' + line.query + '".');
        return id;
      }

      const selected = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (selected && /^\d+$/.test(String(selected.value || '')) && Number(selected.value) > 0) {
        return Number(selected.value);
      }

      const product = await getCurrentProductJson(signal);
      const id = firstUsableVariantId(product);
      if (!id) throw new Error('No valid Shopify variant found for the current product.');
      return id;
    };

    await register({
      name: 'azeo_get_page_context',
      description: 'Return the current Azeo storefront page title, URL, path and detected page type.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, consequentialHint: false, untrustedContentHint: false },
      execute: async () => {
        const bodyClass = document.body ? document.body.className : '';
        const match = String(bodyClass).match(/template-([a-z0-9_-]+)/i);
        return JSON.stringify({
          title: document.title,
          url: location.href,
          path: location.pathname,
          pageType: match ? match[1] : 'unknown'
        });
      }
    });

    await register({
      name: 'azeo_get_current_product',
      description: 'Return structured information about the product displayed on the current Azeo product page. variants[].gid is the exact ProductVariant GID to use for cart.line_items[].item.id.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, consequentialHint: false, untrustedContentHint: false },
      execute: async (_input, { signal } = {}) => {
        const product = await getCurrentProductJson(signal);
        return JSON.stringify({
          id: product.id,
          title: product.title,
          handle: product.handle,
          vendor: product.vendor,
          price: product.price,
          available: product.available,
          variants: (product.variants || []).map((variant) => ({
            id: variant.id,
            gid: 'gid://shopify/ProductVariant/' + variant.id,
            title: variant.title,
            sku: variant.sku || null,
            price: variant.price,
            available: variant.available
          })),
          url: location.href
        });
      }
    });

    await register({
      name: 'azeo_search_products',
      description: 'Search the Azeo Shopify catalog and return matching products with handles and storefront URLs.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Product search terms.' },
          limit: { type: 'integer', minimum: 1, maximum: 10, default: 5 }
        },
        required: ['query'],
        additionalProperties: false
      },
      annotations: { readOnlyHint: true, consequentialHint: false, untrustedContentHint: false },
      execute: async ({ query, limit = 5 }, { signal } = {}) => {
        const products = await searchCatalogProducts(query, limit, signal);
        return JSON.stringify(products.map((product) => ({
          title: product.title,
          handle: product.handle || null,
          url: product.url ? new URL(product.url, location.origin).href : null,
          price: product.price || null,
          vendor: product.vendor || null
        })));
      }
    });

    await register({
      name: 'azeo_get_cart',
      description: 'Return the current Shopify cart with line keys, variant IDs, quantities, prices and totals.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, consequentialHint: false, untrustedContentHint: false },
      execute: async (_input, { signal } = {}) => {
        const cart = await jsonFetch('/cart.js', { signal });
        return JSON.stringify({
          token: cart.token,
          item_count: cart.item_count,
          total_price: cart.total_price,
          currency: cart.currency,
          items: (cart.items || []).map((item) => ({
            key: item.key,
            variant_id: item.variant_id,
            variant_gid: 'gid://shopify/ProductVariant/' + item.variant_id,
            product_id: item.product_id,
            title: item.product_title || item.title,
            variant_title: item.variant_title,
            quantity: item.quantity,
            price: item.price,
            final_line_price: item.final_line_price,
            url: item.url
          }))
        });
      }
    });

    await register({
      name: 'azeo_add_to_cart',
      description: 'Add one or more products to the Azeo Shopify cart. Preferred WebMCP input is cart.line_items[]. Use item.id with a real ProductVariant GID when known; otherwise handle or query may be used. Legacy variant_id input remains supported. Placeholder IDs such as gid://shopify/ProductVariant/0 are ignored and resolved safely from handle, query, or the current product page.',
      inputSchema: {
        type: 'object',
        properties: {
          cart: {
            type: 'object',
            properties: {
              line_items: {
                type: 'array',
                minItems: 1,
                maxItems: 10,
                items: {
                  type: 'object',
                  properties: {
                    item: {
                      type: 'object',
                      properties: {
                        id: { type: 'string', description: 'Real Shopify ProductVariant GID.' }
                      },
                      additionalProperties: false
                    },
                    handle: { type: 'string', description: 'Shopify product handle.' },
                    query: { type: 'string', description: 'Catalog search terms.' },
                    quantity: { type: 'integer', minimum: 1, maximum: 99, default: 1 }
                  },
                  additionalProperties: false
                }
              }
            },
            required: ['line_items'],
            additionalProperties: false
          },
          variant_id: { type: 'string', description: 'Legacy Shopify numeric variant ID or ProductVariant GID.' },
          quantity: { type: 'integer', minimum: 1, maximum: 99, default: 1 }
        },
        additionalProperties: false
      },
      annotations: { readOnlyHint: false, consequentialHint: true, untrustedContentHint: false },
      execute: async (input = {}, { signal } = {}) => {
        const requestedLines = input.cart && Array.isArray(input.cart.line_items) && input.cart.line_items.length
          ? input.cart.line_items
          : [{
              item: input.variant_id ? { id: input.variant_id } : {},
              quantity: input.quantity || 1
            }];

        const items = [];
        for (const line of requestedLines) {
          const quantity = Number.isInteger(line.quantity) && line.quantity > 0 ? Math.min(line.quantity, 99) : 1;
          let resolvedVariantId;
          try {
            resolvedVariantId = await resolveVariantId(line, signal);
          } catch (error) {
            throw new Error('[AZEO resolveVariantId] ' + (error && error.message ? error.message : String(error)));
          }
          items.push({ id: resolvedVariantId, quantity });
        }

        let result;
        try {
          result = await jsonFetch('/cart/add.js', {
            method: 'POST',
            signal,
            body: JSON.stringify({ items })
          });
        } catch (error) {
          throw new Error('[AZEO cart/add.js] ' + (error && error.message ? error.message : String(error)));
        }

        return JSON.stringify({
          added: true,
          requested_count: items.length,
          added_items: items.map((entry) => ({
            variant_id: entry.id,
            variant_gid: 'gid://shopify/ProductVariant/' + entry.id,
            quantity: entry.quantity
          })),
          items: result.items || result
        });
      }
    });

    await register({
      name: 'azeo_remove_from_cart',
      description: 'Remove one line from the Azeo Shopify cart using its cart line key.',
      inputSchema: {
        type: 'object',
        properties: {
          line_key: { type: 'string', description: 'Cart line key returned by azeo_get_cart.' }
        },
        required: ['line_key'],
        additionalProperties: false
      },
      annotations: { readOnlyHint: false, consequentialHint: true, untrustedContentHint: false },
      execute: async ({ line_key }, { signal } = {}) => {
        const cart = await jsonFetch('/cart/change.js', {
          method: 'POST',
          signal,
          body: JSON.stringify({ id: line_key, quantity: 0 })
        });
        return JSON.stringify({
          removed: true,
          item_count: cart.item_count,
          total_price: cart.total_price,
          items: cart.items || []
        });
      }
    });

    await register({
      name: 'azeo_update_cart_quantity',
      description: 'Update the quantity of one Azeo Shopify cart line using its cart line key. Quantity 0 removes the line.',
      inputSchema: {
        type: 'object',
        properties: {
          line_key: { type: 'string', description: 'Cart line key returned by azeo_get_cart.' },
          quantity: { type: 'integer', minimum: 0, maximum: 99 }
        },
        required: ['line_key', 'quantity'],
        additionalProperties: false
      },
      annotations: { readOnlyHint: false, consequentialHint: true, untrustedContentHint: false },
      execute: async ({ line_key, quantity }, { signal } = {}) => {
        const cart = await jsonFetch('/cart/change.js', {
          method: 'POST',
          signal,
          body: JSON.stringify({ id: line_key, quantity })
        });
        return JSON.stringify({
          updated: true,
          line_key,
          quantity,
          item_count: cart.item_count,
          total_price: cart.total_price,
          items: cart.items || []
        });
      }
    });

    console.info('[AZEO WebMCP] 7 tools registered - cart compatibility SAFE v2');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', registerAzeoWebMCP, { once: true });
  } else {
    registerAzeoWebMCP();
  }
})();